import { Layer } from '@deck.gl/core';
import { PathStyleExtension } from '@deck.gl/extensions';
import { GeoJsonLayer, IconLayer, PathLayer, ScatterplotLayer, TextLayer } from '@deck.gl/layers';
import type { MapboxOverlayProps } from '@deck.gl/mapbox';
import { MapboxOverlay } from '@deck.gl/mapbox';
import { css } from '@emotion/react';
import { useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react';
import type { FeatureCollection, Polygon } from 'geojson';
import 'maplibre-gl/dist/maplibre-gl.css';
import React from 'react';
import Map, { NavigationControl, useControl, useMap } from 'react-map-gl/maplibre';
import { ActiveLocationType } from '../../common/active-location';
import { AirQualityMarkerProperties } from '../../common/analytics/analytics-properties/air-quality-marker';
import { AnalyticsEventName } from '../../common/analytics/types';
import { trackEvent } from '../../common/analytics/utils';
import { htmlColorToRgbArray } from '../../common/aqi';
import { getTextColorBW } from '../../common/color';
import {
  MAP_MARKER_TEXT_SIZE,
  MAP_STYLE,
  MAX_MAP_MARKER_SIZE,
  MAX_MAP_ZOOM,
  MIN_MAP_MARKER_SIZE,
  REFERENCE_MAX_ZOOM,
  REFERENCE_MIN_ZOOM,
} from '../../common/constants';
import { MapData } from '../../common/map-data';
import { useSettings } from '../../contexts/SettingsContext';
import { type SourceType } from '../../types';
import { IconSource } from '../../types/icon-source';
import LablededScatterPlotLayer, { MapMarkerType } from '../LabeledScatterplotLayer';
import MapMarkerDot from '../MapMarkerDot';
import MapMarkerPin from '../MapMarkerPin';
import { Spinner } from '../Spinner';
import './maplibre.override.css';

const DeckGLOverlay: React.FC<MapboxOverlayProps> = (props) => {
  const overlay = useControl(() => new MapboxOverlay(props));
  overlay.setProps(props);
  return null;
};

interface MaplibreMapProps {
  data?: MapData[];
  enableDragPan?: boolean;
  showZoomControl?: boolean;
  enableClickAgainToClearMarker?: boolean;
  onContainerResize?: (width: number, height: number) => void;
}

const MaplibreMap: React.FC<MaplibreMapProps> = ({
  data,
  enableDragPan = true,
  showZoomControl = true,
  enableClickAgainToClearMarker = true,
  onContainerResize,
}) => {
  const { aqMap: map } = useMap();
  const {
    mapSettings,
    aqiStandard,
    marker,
    pollutantConfigs,
    activeLocation,
    viewport,
    network,
    openSiteRadius,
    setViewport,
    setDatasourceId,
  } = useSettings();

  const mapMarkerComponent = React.useMemo(() => {
    if (!activeLocation) {
      return null;
    }
    const { point } = activeLocation;
    const [longitude, latitude] = point.coordinates;
    const sharedProps = { longitude, latitude };
    switch (activeLocation.type) {
      case ActiveLocationType.MyLocation:
        return <MapMarkerDot {...sharedProps} />;
      case ActiveLocationType.ReverseGeocodedLocation:
        return <MapMarkerPin {...sharedProps} />;
      default:
        return null;
    }
  }, [activeLocation]);

  /**
   * Returns a radius in pixels that scales linearly between
   * MIN_MAP_MARKER_SIZE and MAX_MAP_MARKER_SIZE as the map zooms
   * from MIN_MAP_ZOOM to MAX_MAP_ZOOM. Values outside that range
   * are clamped so the marker never becomes smaller/larger than
   * the predefined bounds.
   */
  const markerRadius = React.useMemo(() => {
    const zoom = viewport?.zoom;

    // Fallback to the largest size when zoom is undefined (initial load)
    if (zoom === undefined) {
      return MAX_MAP_MARKER_SIZE;
    }

    // Normalise zoom to the 0..1 range, then interpolate size
    const t = (zoom - REFERENCE_MIN_ZOOM) / (REFERENCE_MAX_ZOOM - REFERENCE_MIN_ZOOM);
    const clampedT = Math.max(0, Math.min(1, t));
    return MIN_MAP_MARKER_SIZE + clampedT * (MAX_MAP_MARKER_SIZE - MIN_MAP_MARKER_SIZE);
  }, [viewport?.zoom]);

  // Add openSiteLayer as a deck.gl layer
  const openSiteLayer = React.useMemo(() => {
    if (!activeLocation || activeLocation.type !== ActiveLocationType.OpenSite) {
      return null;
    }
    // Get the icon info
    const icon = network?.defaultOpenSiteIcon;
    if (!icon) {
      return null;
    }

    // Determine icon URL
    let iconUrl = '';
    if (icon.iconSource === IconSource.Url) {
      iconUrl = icon.iconUrl;
    } else {
      // Not supported
      return null;
    }

    const openSiteIconSize = 32;
    const [longitude, latitude] = activeLocation.point.coordinates;
    return new IconLayer({
      id: 'open-site-icon-layer',
      data: [{ position: [longitude, latitude], iconUrl }],
      getPosition: (d) => d.position,
      getIcon: (d) => ({
        url: d.iconUrl,
        width: openSiteIconSize,
        height: openSiteIconSize,
        anchorY: openSiteIconSize,
      }),
      sizeUnits: 'pixels',
      getSize: openSiteIconSize,
      pickable: false,
    });
  }, [activeLocation, network?.defaultOpenSiteIcon]);

  // Force the map to be unmounted when leaving the page
  // to avoid an error with multiple map instances.
  const [showMap, setShowMap] = React.useState(true);
  useIonViewWillLeave(() => {
    setShowMap(false);
  }, []);
  useIonViewWillEnter(() => {
    setShowMap(true);
  }, []);

  // Resize the map when the view is entered
  React.useEffect(() => {
    if (showMap) {
      map?.resize();
    }
  }, [map, showMap]);

  const sizeCbRef = React.useRef(onContainerResize);

  // Update refs when props change (does not recreate observer)
  React.useEffect(() => {
    sizeCbRef.current = onContainerResize;
  }, [onContainerResize]);

  // Observe the container size changes
  React.useEffect(() => {
    if (!map) {
      return;
    }

    const container = map.getContainer();
    if (!container) {
      return;
    }

    const notify = () => {
      const { clientWidth: w, clientHeight: h } = container;
      if (sizeCbRef.current) {
        sizeCbRef.current(w, h);
      }
    };

    notify(); // initial

    const resizeObserver = new ResizeObserver(notify);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [map]);

  /**
   * Show text labels only when zoomed in beyond a threshold to
   * avoid clutter when the map is zoomed out.
   */
  const markerTextSize = React.useMemo(() => {
    const zoom = viewport?.zoom ?? 0;
    return zoom > 12 ? MAP_MARKER_TEXT_SIZE : 0;
  }, [viewport?.zoom]);

  const createClaritySourceLayer = React.useCallback(
    (sourceType: SourceType, data: MapData[]) => {
      if (!aqiStandard || !pollutantConfigs) {
        return null;
      }

      return new LablededScatterPlotLayer<MapData>({
        id: sourceType,
        data: data.filter((item) => item.sourceType === sourceType),
        // map marker
        markerType: sourceType === 'CLARITY_NODE' ? MapMarkerType.Circle : MapMarkerType.Square,
        getPosition: (d: MapData) => {
          // lng, lat, z-index (to avoid overlapping)
          // z-index = 0.1 * index + 10 if the point is selected
          return [...d.position, 0.1 * d.index + (d.datasourceId === marker?.datasourceId ? 10 : 0)];
        },
        getRadius: markerRadius,
        radiusUnits: 'pixels',
        getFillColor: (d) => htmlColorToRgbArray(d.getColor(aqiStandard, pollutantConfigs)), // Color based on AQI
        getLineColor: (d) => (d.datasourceId === marker?.datasourceId ? [0, 0, 0] : [255, 255, 255]),
        stroked: true, // Enable stroke (border)
        lineWidthMinPixels: 2, // Border width
        radiusScale: 2, // Increase for a larger marker size
        pickable: true,
        onClick: ({ object }) => {
          const selectedDatasourceId = object?.datasourceId;
          const selectedMarker = data.find(({ marker }) => marker.datasourceId === selectedDatasourceId)?.marker;
          const newDatasourceId =
            enableClickAgainToClearMarker && marker?.datasourceId === selectedMarker?.datasourceId
              ? null
              : (selectedMarker?.datasourceId ?? null);
          if (selectedMarker) {
            const eventProperties: AirQualityMarkerProperties = {
              datasourceId: selectedDatasourceId,
              sourceType: selectedMarker.sourceType,
              datasourceName: selectedMarker.datasourceName,
              sampledAt: selectedMarker.sampledAt,
              location: selectedMarker.location,
            };
            trackEvent(
              newDatasourceId ? AnalyticsEventName.SelectedMapMarker : AnalyticsEventName.ClearedMapMarker,
              eventProperties
            );
          }
          setDatasourceId(newDatasourceId);
        },
        // text
        getText: (d) => d.getMainPollutantAqiDisplayValue(pollutantConfigs, aqiStandard),
        getTextSize: markerTextSize,
        getTextColor: (f) => htmlColorToRgbArray(getTextColorBW(f.getColor(aqiStandard, pollutantConfigs))),
        getTextAnchor: 'middle',
        getTextAlignmentBaseline: 'center',
      });
    },
    [marker?.datasourceId, aqiStandard, showMap, pollutantConfigs, markerRadius, markerTextSize]
  );

  const createPathLayer = React.useCallback(() => {
    if (!marker || !activeLocation || !viewport) {
      return null;
    }

    if (
      ![
        ActiveLocationType.MyLocation,
        ActiveLocationType.ReverseGeocodedLocation,
        ActiveLocationType.OpenSite,
      ].includes(activeLocation.type)
    ) {
      return null;
    }

    const getPathWidth = () => {
      const baseWidth = 4; // Base width for the path
      const zoomFactor = 156412 / Math.pow(2, viewport.zoom); // Adjust width based on zoom level
      return baseWidth * zoomFactor;
    };

    return new PathLayer({
      id: 'path-layer',
      data: [
        {
          path: [activeLocation.point.coordinates, marker.location.coordinates],
          name: 'Dash Line',
        },
      ],
      getPath: (d) => d.path,
      getColor: [173, 216, 230],
      getWidth: getPathWidth,
      getDashArray: [4, 2], // Dash pattern: 4px dash, 2px gap
      getOpacity: 0.8,
      getPolygonOffset: () => [0, 0],
      extensions: [new PathStyleExtension({ dash: true })],
    });
  }, [activeLocation, marker, viewport?.zoom, showMap]);

  const createBboxLayers = React.useCallback(() => {
    if (!mapSettings?.bbox) {
      return null;
    }
    const { bbox } = mapSettings;
    const [[minLng, minLat], [maxLng, maxLat]] = bbox;
    const coordinates = [
      [
        [minLng, minLat], // Bottom-left
        [maxLng, minLat], // Bottom-right
        [maxLng, maxLat], // Top-right
        [minLng, maxLat], // Top-left
        [minLng, minLat], // Close the polygon
      ],
    ];

    const bboxLayer = new GeoJsonLayer({
      id: 'boundingBoxLayer',
      data: {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates,
            },
          },
        ],
      } as FeatureCollection<Polygon>,
      stroked: true,
      lineWidthScale: 1,
      lineWidthMinPixels: 2,
      getLineColor: [255, 0, 0], // Red border
      getLineWidth: 3, // Border thickness
      getFillColor: [0, 0, 0, 0], // Transparent fill (RGBA)
    });

    const labelLayer = new TextLayer({
      id: 'boundingBoxLabelLayer',
      data: [{ position: [maxLng, maxLat], text: 'Bounding Box' }],
      getPosition: (d) => d.position,
      getText: (d) => d.text,
      getSize: 8,
      getColor: [255, 0, 0], // Red text
      getTextAnchor: 'start',
      getAlignmentBaseline: 'top',
      getPixelOffset: [5, 0],
    });

    return [bboxLayer, labelLayer];
  }, [mapSettings?.bbox]);

  const createOpenSiteRadiusLayer = React.useCallback((): Layer | null => {
    if (
      !activeLocation ||
      activeLocation.type !== ActiveLocationType.OpenSite ||
      !openSiteRadius ||
      !aqiStandard ||
      !pollutantConfigs ||
      !marker
    ) {
      return null;
    }
    const aqiColor = htmlColorToRgbArray(new MapData(0, marker).getColor(aqiStandard, pollutantConfigs));
    const fillColor = [...aqiColor, 40] as [number, number, number, number];
    const lineColor = [...aqiColor, 80] as [number, number, number, number];

    const [longitude, latitude] = activeLocation.point.coordinates;

    // Convert km to meters and round for display
    const radiusMeters = Math.round(openSiteRadius * 1000);

    // Scatterplot layer representing the circular radius
    const radiusLayer = new ScatterplotLayer({
      id: 'open-site-radius-layer',
      data: [{ position: [longitude, latitude] }],
      getPosition: (d) => d.position,
      getRadius: radiusMeters, // meters
      radiusUnits: 'meters',
      stroked: true,
      getFillColor: fillColor,
      getLineColor: lineColor,
      lineWidthMinPixels: 2,
      pickable: false,
    });

    return radiusLayer;
  }, [activeLocation, openSiteRadius, marker]);

  const layers = React.useMemo((): Layer[] => {
    if (!data) {
      return [];
    }
    const refSiteLayer = createClaritySourceLayer('REFERENCE_SITE' as SourceType, data);
    const clarityNodeLayer = createClaritySourceLayer('CLARITY_NODE' as SourceType, data);
    const pathLayer = createPathLayer();
    const openSiteRadiusLayer = createOpenSiteRadiusLayer();

    const layers: (Layer | null)[] = [openSiteRadiusLayer, openSiteLayer, refSiteLayer, clarityNodeLayer, pathLayer];

    const showDebugLayers =
      (import.meta.env.DEV &&
        !window.location.host.includes('dmo') &&
        !window.location.host.includes('lausd.map.clarity.io')) ||
      window.location.host.includes('localhost');
    if (showDebugLayers) {
      const bboxLayers = createBboxLayers();
      layers.push(...(bboxLayers ?? []));
    }
    return layers.filter((layer) => layer !== null);
  }, [data, openSiteLayer, createClaritySourceLayer, createPathLayer, createOpenSiteRadiusLayer, createBboxLayers]);

  const maplibreMapCss = {
    spinnerBackdrop: [
      css({
        position: 'absolute',
        height: '100%',
        width: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        zIndex: 2,
      }),
    ],
    spinnerContainer: [
      css({
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }),
    ],
  };

  return (
    <>
      {!data && (
        <div css={maplibreMapCss.spinnerBackdrop}>
          <div css={maplibreMapCss.spinnerContainer}>
            <Spinner type="dots" color="light" size="4rem" />
          </div>
        </div>
      )}
      {showMap && viewport && (
        <Map
          id="aqMap" // note: the tour feature uses this id
          {...viewport}
          bearing={0} // init map rotation
          pitch={0} // init map pitch
          dragRotate={false} // disable drag rotation
          keyboard={false} // disable keyboard rotation
          touchPitch={false} // disable touch pitch
          pitchWithRotate={false} // disable pitch with rotate
          onMove={(evt) => setViewport(evt.viewState)}
          mapStyle={MAP_STYLE}
          maxZoom={MAX_MAP_ZOOM}
          dragPan={enableDragPan}
          scrollZoom={showZoomControl}
          doubleClickZoom={showZoomControl}
        >
          <NavigationControl showCompass={false} showZoom={showZoomControl} />
          <DeckGLOverlay layers={layers} interleaved />
          {data && mapMarkerComponent}
        </Map>
      )}
    </>
  );
};

export default MaplibreMap;
